/**
 * GlassIconButton — Reusable round glass-morphism icon button.
 *
 * Used for all floating overlay controls in the 3D Viewer:
 *   - Return button (top-left)
 *   - AR button (top-right)
 *   - Future: screenshot, share, etc.
 *
 * Design spec (glass-dark tier):
 *   bg-black/60 backdrop-blur-[10px] rounded-full
 *   drop-shadow-[0_4px_10px_rgba(0,0,0,0.4)]
 *   hover/active: bg-black
 *   Icon: centered, 20×20px, white
 *
 * AR ripple variant: pass `ripple` to show the infinite pulse ring.
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#top-leiste-immer-sichtbar-nach-load
 * @see docs/ims-brand-frontends/03-design-system.md#glass-morphism--experience-design-sprache
 */
import { useState } from 'react';
import { cn } from '../../utils/cn';
import { useUiTheme } from '../../hooks/useUiTheme';

interface GlassIconButtonProps {
  /** SVG icon URL (imported from /app/icons/). */
  icon: string;
  /** Accessible label for screen readers. */
  label: string;
  onClick?: () => void;
  /** Show the infinite ripple/pulse ring (for AR button). */
  ripple?: boolean;
  className?: string;
  disabled?: boolean;
}

/**
 * CSS keyframes string — injected once into <head> on first render.
 * Produces a subtle expanding white ring from the button edge, 2 s loop.
 */
const RIPPLE_KEYFRAMES = `
@keyframes _glassRipple {
  0%   { box-shadow: 0 0 0 0px rgba(255,255,255,0.60); opacity: 1; }
  70%  { box-shadow: 0 0 0 18px rgba(255,255,255,0); opacity: 0; }
  100% { box-shadow: 0 0 0 0px rgba(255,255,255,0); opacity: 0; }
}
`;

let keyframesInjected = false;
function injectKeyframes() {
  if (keyframesInjected) return;
  keyframesInjected = true;
  const style = document.createElement('style');
  style.textContent = RIPPLE_KEYFRAMES;
  document.head.appendChild(style);
}

export function GlassIconButton({
  icon,
  label,
  onClick,
  ripple = false,
  className,
  disabled = false,
}: GlassIconButtonProps) {
  const [pressed, setPressed] = useState(false);
  const theme = useUiTheme();

  if (ripple) injectKeyframes();

  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      className={cn(
        // Base — round, glass
        'relative flex items-center justify-center',
        'w-10 h-10 rounded-full',
        theme.glassBtn, 'backdrop-blur-[10px]',
        'drop-shadow-[0_4px_10px_rgba(0,0,0,0.4)]',
        // Transitions
        'transition-[background-color,transform] duration-200',
        'transition-colors duration-700',
        // Hover / Active
        !pressed ? theme.glassBtnHover : theme.glassBtnPressed,
        pressed && 'scale-98',
        // Disabled
        disabled && 'opacity-40 cursor-not-allowed pointer-events-none',
        className,
      )}
    >
      {/* Icon */}
      <img
        src={icon}
        alt=""
        aria-hidden="true"
        className="w-6 h-6 select-none pointer-events-none"
        draggable={false}
      />

      {/* Ripple ring — pseudo-element via inline pseudo-sibling */}
      {ripple && (
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            animation: '_glassRipple 2s cubic-bezier(0.6, 0, 0.8, 1) infinite',
          }}
        />
      )}
    </button>
  );
}
